// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000',
  API_VERSION: 'v1',
  TIMEOUT: 30000, // 30 seconds
  RETRY_ATTEMPTS: 3,
} as const;

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  LOGIN: 'auth/login',
  LOGOUT: 'auth/logout',
  ME: 'auth/me',
  REFRESH: 'auth/refresh',
  
  // Users
  USERS: 'users',
  USER_PERMISSIONS: (id: number) => `users/${id}/permissions`,
  USER_BULK_PERMISSIONS: (id: number) => `users/${id}/permissions/bulk`,
  USER_BULK_ASSIGN: 'users/bulk-assign-modules',
  USER_BULK_REMOVE: 'users/bulk-remove-modules',
  
  // Groups
  GROUPS: 'groups',
  GROUP_PERMISSIONS: (id: number) => `groups/${id}/permissions`,
  GROUP_BULK_PERMISSIONS: (id: number) => `groups/${id}/permissions/bulk`,
  GROUP_BULK_ASSIGN: 'groups/bulk-assign-modules',
  GROUP_BULK_REMOVE: 'groups/bulk-remove-modules',
  
  // Modules
  MODULES: 'modules',
  MODULES_HIERARCHICAL: 'modules/hierarchical',
  
  // Call Logs
  CALL_LOGS: 'call-logs',
  CALL_LOGS_STATS: 'call-logs/stats',
  CALL_LOGS_SYNC: 'call-logs/sync',
  
  // Contacts
  CONTACTS: 'contacts',
  
  // Dashboard
  DASHBOARD_STATS: 'dashboard/stats',
  DASHBOARD_AGENT_STATS: 'dashboard/agent-stats',
  
  // Profile
  PROFILE: 'profile',
  PROFILE_UPDATE: 'profile/update',
  PROFILE_CHANGE_PASSWORD: 'profile/change-password',
  
  // Reports
  REPORTS: 'reports',
  AGENT_PERFORMANCE_REPORT: 'agent-performance-report',
  AGENT_CALL_COUNT_REPORT: 'agent-call-count-report',
  AGENT_CALL_LIST_REPORT: 'agent-call-list-report',
} as const;

// Application Configuration
export const APP_CONFIG = {
  NAME: 'Sales Agent & Commission App',
  VERSION: '1.0.0',
  DESCRIPTION: 'Comprehensive sales agent and commission management system',
  AUTHOR: 'ATNR Team',
} as const;

// Feature Flags
export const FEATURES = {
  DARK_MODE: true,
  EXPORT_FUNCTIONALITY: true,
  BULK_OPERATIONS: true,
  REAL_TIME_UPDATES: false, // TODO: Enable when implementing real-time features
  OFFLINE_MODE: false, // TODO: Enable when implementing offline capability
} as const;

// Pagination Configuration
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 15,
  PAGE_SIZE_OPTIONS: [10, 15, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

// Date Formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  DISPLAY_WITH_TIME: 'MMM dd, yyyy HH:mm',
  API: 'yyyy-MM-dd',
  API_WITH_TIME: 'yyyy-MM-dd HH:mm:ss',
  ISO: 'yyyy-MM-ddTHH:mm:ss.SSSZ',
} as const;

// Status Colors
export const STATUS_COLORS = {
  completed: {
    light: 'bg-green-100 text-green-800',
    dark: 'dark:bg-green-900 dark:text-green-300',
  },
  missed: {
    light: 'bg-red-100 text-red-800',
    dark: 'dark:bg-red-900 dark:text-red-300',
  },
  rejected: {
    light: 'bg-orange-100 text-orange-800',
    dark: 'dark:bg-orange-900 dark:text-orange-300',
  },
  in_progress: {
    light: 'bg-blue-100 text-blue-800',
    dark: 'dark:bg-blue-900 dark:text-blue-300',
  },
  pending: {
    light: 'bg-yellow-100 text-yellow-800',
    dark: 'dark:bg-yellow-900 dark:text-yellow-300',
  },
} as const;

// Permission Actions
export const PERMISSION_ACTIONS = {
  ADD: 'add',
  EDIT: 'edit',
  DELETE: 'delete',
  PRINT: 'print',
  EXPORT: 'export',
  VIEW: 'view',
} as const;

// Module Names
export const MODULES = {
  USERS: 'users',
  GROUPS: 'groups',
  MODULES: 'modules',
  CALLS: 'calls',
  CONTACTS: 'contacts',
  REPORTS: 'reports',
  DASHBOARD: 'dashboard',
  PROFILE: 'profile',
  PERMISSIONS: 'permissions',
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_DATA: 'user_data',
  THEME: 'theme',
  LANGUAGE: 'language',
  SIDEBAR_COLLAPSED: 'sidebar_collapsed',
  TABLE_PREFERENCES: 'table_preferences',
  DASHBOARD_STATS: 'dashboard_stats',
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  FORBIDDEN: 'Access denied. You don\'t have permission for this resource.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  TIMEOUT_ERROR: 'Request timeout. Please try again.',
  UNKNOWN_ERROR: 'An unexpected error occurred.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  CREATED: 'Record created successfully.',
  UPDATED: 'Record updated successfully.',
  DELETED: 'Record deleted successfully.',
  SAVED: 'Changes saved successfully.',
  EXPORTED: 'Data exported successfully.',
  IMPORTED: 'Data imported successfully.',
  LOGIN_SUCCESS: 'Login successful.',
  LOGOUT_SUCCESS: 'Logout successful.',
  PASSWORD_CHANGED: 'Password changed successfully.',
} as const;

// Validation Rules
export const VALIDATION_RULES = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  NAME_MIN_LENGTH: 2,
  NAME_MAX_LENGTH: 100,
  EMAIL_MAX_LENGTH: 255,
  PHONE_MAX_LENGTH: 20,
  NOTES_MAX_LENGTH: 1000,
} as const;

// Export Formats
export const EXPORT_FORMATS = {
  CSV: 'csv',
  JSON: 'json',
  EXCEL: 'xlsx',
  PDF: 'pdf',
} as const;

// Chart Colors
export const CHART_COLORS = [
  '#3B82F6', // blue-500
  '#10B981', // emerald-500
  '#F59E0B', // amber-500
  '#EF4444', // red-500
  '#8B5CF6', // violet-500
  '#06B6D4', // cyan-500
  '#84CC16', // lime-500
  '#F97316', // orange-500
  '#EC4899', // pink-500
  '#6B7280', // gray-500
] as const;

// Utility function to get full API URL
export const getApiUrl = (endpoint: string): string => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${API_CONFIG.BASE_URL}/api/${API_CONFIG.API_VERSION}/${cleanEndpoint}`;
};

// Utility function to get status color classes
export const getStatusColorClasses = (status: string, isDark: boolean = false): string => {
  const statusConfig = STATUS_COLORS[status as keyof typeof STATUS_COLORS];
  if (!statusConfig) {
    return isDark ? 'dark:bg-gray-700 dark:text-gray-300' : 'bg-gray-100 text-gray-800';
  }
  return isDark ? statusConfig.dark : statusConfig.light;
};

// Utility function to format duration
export const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '0m 0s';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${secs}s`;
  }
  return `${minutes}m ${secs}s`;
};

// Utility function to format date
export const formatDate = (dateString: string, format: keyof typeof DATE_FORMATS = 'DISPLAY'): string => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  
  // Simple date formatting - in a real app, you'd use a library like date-fns
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: format.includes('MMM') ? 'short' : '2-digit',
    day: '2-digit',
  };
  
  if (format.includes('TIME')) {
    options.hour = '2-digit';
    options.minute = '2-digit';
  }
  
  return date.toLocaleDateString('en-US', options);
}; 